/**
 * CommunityMyProfilePage — §11 My Profile.
 * Shows the volunteer's Name + Photo (editable via the existing UserController
 * `PUT /users/me` + `POST /users/me/profile-image`, synced to the auth store) and
 * their membership profile — location, languages, skills, interests, availability,
 * volunteer experience, preferred activity type — editable via
 * `PUT /community/me/member-profile`. Deliberately collects NO medical
 * information. No financial concepts.
 */
import { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { UserCircle, Camera, Loader2, Check, Link2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { CommunityLayout, GovPageTitle, GovCard, GovButton } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading, Avatar } from '@/components/community/ui'
import { useAuthStore } from '@/stores/authStore'
import userService from '@/services/userService'
import { getApiErrorMessage } from '@/services/api'
import {
  getMembers, updateMyMemberProfile, type MemberResponse, type MemberProfileRequest,
} from '@/services/communityApi'

const INTEREST_TAGS = [
  'Awareness Drives', 'Community Outreach', 'Digital Awareness', 'College Outreach',
  'Family Support', 'Stigma Reduction', 'Content Creation', 'Event Planning',
  'Translation', 'Counselling',
]
const ACTIVITY_TYPES = ['Awareness Sessions', 'Community Outreach', 'Digital Awareness', 'College Outreach', 'Family Support']

export default function CommunityMyProfilePage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const { user, updateProfile } = useAuthStore()
  const [member, setMember] = useState<MemberResponse | null>(null)
  const [loading, setLoading] = useState(true)

  // Name editing (User table)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [nameDirty, setNameDirty] = useState(false)
  const [savingName, setSavingName] = useState(false)

  // Membership fields
  const [form, setForm] = useState<MemberProfileRequest>({})
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Photo upload
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const load = useCallback(async () => {
    if (!community || !user) return
    setLoading(true)
    try {
      const members = await getMembers(community.id)
      const mine = members.find((m) => m.user.id === user.id) ?? null
      setMember(mine)
      if (mine) {
        setForm({
          location: mine.location ?? '', languages: mine.languages ?? [], skills: mine.skills ?? [],
          interests: mine.interests ?? [], availability: mine.availability ?? '',
          volunteerExperience: mine.volunteerExperience ?? '', preferredActivityType: mine.preferredActivityType ?? '',
        })
      }
    } catch { /* ignore */ } finally { setLoading(false) }
  }, [community, user])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    if (user) { setFirstName(user.firstName ?? ''); setLastName(user.lastName ?? '') }
  }, [user])

  const onNameChange = (which: 'firstName' | 'lastName', v: string) => {
    if (which === 'firstName') setFirstName(v); else setLastName(v)
    setNameDirty(true)
  }
  const saveName = async () => {
    setSavingName(true)
    try {
      const upd = await userService.updateProfile({ firstName, lastName })
      updateProfile(upd)
      setNameDirty(false)
    } catch { /* ignore */ } finally { setSavingName(false) }
  }

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 20 * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(1)
      alert(`This image is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller image.`)
      e.target.value = ''
      return
    }
    setUploading(true)
    try {
      const { url } = await userService.uploadProfileImage(file)
      if (user) updateProfile({ profileImage: url })
    } catch (err) {
      alert(getApiErrorMessage(err, 'Failed to upload profile image. Please try again.'))
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = '' }
  }

  const field = (k: keyof MemberProfileRequest, v: any) => {
    setForm((p) => ({ ...p, [k]: v })); setDirty(true); setSaved(false)
  }
  const toggleArrayItem = (k: 'skills' | 'interests' | 'languages', tag: string) => {
    const cur = (form[k] as string[]) ?? []
    field(k, cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag])
  }
  const saveMember = async () => {
    setSaving(true)
    try {
      const upd = await updateMyMemberProfile(form)
      setMember(upd)
      setDirty(false); setSaved(true)
    } catch { /* ignore */ } finally { setSaving(false) }
  }

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none min-h-[44px]'

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.myProfile.title')} subtitle={t('community.myProfile.subtitle')} icon={UserCircle} />

      {loading || !user ? <SectionLoading rows={3} /> : (
        <div className="space-y-6">
          {/* Name + Photo */}
          <GovCard>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="relative shrink-0">
                <Avatar name={user.fullName} image={user.profileImage} size={72} />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  aria-label={t('community.myProfile.changePhoto')}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow disabled:opacity-50">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Camera className="w-4 h-4" aria-hidden="true" />}
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900">{user.fullName}</h2>
                <p className="text-sm text-gray-500">{user.email}</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.firstName')}</label>
                    <input value={firstName} onChange={(e) => onNameChange('firstName', e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.lastName')}</label>
                    <input value={lastName} onChange={(e) => onNameChange('lastName', e.target.value)} className={inputCls} />
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <button onClick={saveName} disabled={!nameDirty || savingName}
                    className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                    {savingName ? t('common.saving') : t('community.myProfile.saveName')}
                  </button>
                  <Link to={`/profile/${user.username}`} className="text-sm font-medium text-gray-500 hover:underline inline-flex items-center gap-1 min-h-[40px]">
                    <Link2 className="w-4 h-4" aria-hidden="true" />{t('community.myProfile.fullProfile')}
                  </Link>
                </div>
              </div>
            </div>
          </GovCard>

          {/* Membership profile */}
          <GovCard>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700">{t('community.myProfile.volunteerDetails')}</h2>
              {(dirty || saved) && (
                <button onClick={saveMember} disabled={saving}
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : saved ? <Check className="w-4 h-4 text-emerald-600" aria-hidden="true" /> : null}
                  {saving ? t('common.saving') : saved ? t('community.myProfile.saved') : t('community.myProfile.save')}
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.location')}</label>
                  <input value={form.location ?? ''} onChange={(e) => field('location', e.target.value)} className={inputCls} placeholder={t('community.myProfile.locationPh')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.availability')}</label>
                  <input value={form.availability ?? ''} onChange={(e) => field('availability', e.target.value)} className={inputCls} placeholder={t('community.myProfile.availabilityPh')} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.preferredActivityType')}</label>
                <select value={form.preferredActivityType ?? ''} onChange={(e) => field('preferredActivityType', e.target.value)} className={inputCls}>
                  <option value="">{t('community.myProfile.selectActivityType')}</option>
                  {ACTIVITY_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              <TagField label={t('community.myProfile.languages')} tags={['English', 'हिन्दी', 'मराठी', 'ગુજરાતી', 'বাংলা', 'தமிழ்', 'తెలుగు']}
                selected={(form.languages as string[]) ?? []} onToggle={(tag) => toggleArrayItem('languages', tag)} />

              <TagField label={t('community.myProfile.skills')} tags={['Public Speaking', 'Social Media', 'Event Planning', 'Counselling', 'Design', 'Translation', 'Logistics', 'Photography']}
                selected={(form.skills as string[]) ?? []} onToggle={(tag) => toggleArrayItem('skills', tag)} />

              <TagField label={t('community.myProfile.interests')} tags={INTEREST_TAGS}
                selected={(form.interests as string[]) ?? []} onToggle={(tag) => toggleArrayItem('interests', tag)} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.myProfile.volunteerExperience')}</label>
                <textarea value={form.volunteerExperience ?? ''} onChange={(e) => field('volunteerExperience', e.target.value)} rows={3}
                  className={inputCls} placeholder={t('community.myProfile.experiencePh')} />
              </div>
            </div>
          </GovCard>
        </div>
      )}
    </CommunityLayout>
  )
}

function TagField({ label, tags, selected, onToggle }: {
  label: string; tags: string[]; selected: string[]; onToggle: (tag: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const on = selected.includes(tag)
          return (
            <button key={tag} type="button" onClick={() => onToggle(tag)} aria-pressed={on}
              className={`text-sm font-medium px-3 py-1.5 rounded-full border min-h-[40px] transition-colors ${
                on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
              }`}>
              {tag}
            </button>
          )
        })}
      </div>
    </div>
  )
}